import { useSearchParams } from "next/navigation";
import { CommodityEnumSchema, ProcessEnumSchema, Segment, SegmentEnumSchema, Process } from "@/types/contract";
import { z } from "zod";
import { useContext } from "react";
import { EnumerationsContext } from "@/contexts/EnumerationsContext";
import { Commodity, Process as ProcessEnum, Product, Tariff } from "@/types/enumerations";
import { CustomerType } from "@/types/customerType";
import { Units } from "@/types";
import { parseHash } from "@/utils/searchParams";
import { ProcessReasonType } from "@/types/processReason";
import { getProduct as getContractProduct } from "@/utils/product";
import dayjs from "dayjs";
import { defaultSegmentServiceCompany, defaultSegmentServiceHousehold } from "@/constants/segment";
import { useIsContractDraft } from "@/hooks/useIsContractDraft";

const B2BForsageFeatureFlag = process.env.NEXT_PUBLIC_B2B_FORSAGE_FEATURE_FLAG === "true";

const getCommodity = (commodity: string | null, commodities: Commodity[]): Commodity | null => {
  const parsedCommodity = CommodityEnumSchema.safeParse(commodity);
  return !parsedCommodity.success ? null : commodities.find(c => c.value === parsedCommodity.data) ?? null;
};

const getCustomerType = (customer: string | null, process: string | null): CustomerType => {
  const parsedCustomer = z.enum(["household", "company"]).safeParse(customer);
  const parsedProcess = ProcessEnumSchema.safeParse(process);

  if (!parsedCustomer.success) {
    return "household";
  }

  if (parsedProcess.success && parsedProcess.data === Process.MOBILITY_SERVICES && parsedCustomer.data === "company") {
    return "company";
  }

  return parsedCustomer.data === "company" ? "tradesman" : parsedCustomer.data;
};

const getProcess = (process: string | null, processes: ProcessEnum[]): ProcessEnum | null => {
  const parsedProcess = ProcessEnumSchema.safeParse(process);
  if (!parsedProcess.success) {
    return null;
  }
  return processes.find(proc => proc.value === parsedProcess.data) ?? null;
};

const getSellChannel = (sellChannel: string | null, process: string | null, processes: ProcessEnum[]): string | null => {
  const contractProcess = getProcess(process, processes);
  if (contractProcess?.value === Process.MOBILITY_SERVICES) {
    return "MS";
  }
  return sellChannel ?? null;
};

const getProduct = (
  product: string | null,
  products: Product[],
  commodity: string | null,
  commodities: Commodity[],
  process: string | null,
  processes: ProcessEnum[],
  customer: string | null,
  isContractDraft: boolean
): Product | null => {
  const contractCommodity = getCommodity(commodity, commodities);
  const contractProcess = getProcess(process, processes);
  const contractCustomerType = getCustomerType(customer, process);
  if (!contractCommodity || !contractProcess) {
    return null;
  }
  if (contractProcess.value === Process.NEW_POINT && isContractDraft) {
    return products.find(p => p.value.disId === product) ?? null;
  }

  let customerType = "1";
  if (contractCustomerType === "tradesman" || contractCustomerType === "company") {
    customerType = "2";
  }

  let year = dayjs().get("year");
  if (product?.match(/_20\d{2}$/)) {
    year = Number(product.substring(product.length - 4, product.length));
  }

  const finalProduct = getContractProduct(contractCommodity.value, contractProcess.value, customerType, year, products, product);

  let productWithCustomerType = product;
  if (product) {
    if (contractCustomerType === "tradesman" || contractCustomerType === "company") {
      productWithCustomerType = product.concat("2");
    } else {
      productWithCustomerType = product.concat("1");
    }
  }

  return finalProduct ?? products.find(p => p.id === productWithCustomerType) ?? null;
};

const getValueInBoolean = (value: string | null): boolean => {
  return value === "true";
};

const getYearConsumption = (yearConsumption: string | null, consumptionUnit: string | null): number | null => {
  if (!yearConsumption) {
    return null;
  }
  const numConsumption = Number(yearConsumption);
  if (
    (numConsumption < 0.01 && consumptionUnit === Units.mwh) ||
    (numConsumption < 1 && consumptionUnit === Units.kwh) ||
    (numConsumption < 0.1 && consumptionUnit === Units.m3)
  ) {
    return null;
  }
  return numConsumption;
};

const getTariff = (tariff: string | null, tariffs: Tariff[]): string | null => {
  return tariffs.find(t => t.name === tariff)?.name ?? null;
};

const getSegment = (segment: string | null, customer: string | null, process: string | null): Segment => {
  const contractCustomerType = getCustomerType(customer, process);

  if (B2BForsageFeatureFlag) {
    if (process === Process.MOBILITY_SERVICES) {
      return Segment.B2C;
    }
    if (contractCustomerType === "company" || contractCustomerType === "tradesman") {
      return Segment.B2B;
    } else {
      return Segment.B2C;
    }
  }

  if (!segment) {
    return Segment.B2C;
  }

  const parsedSegment = SegmentEnumSchema.safeParse(segment.toUpperCase());
  return parsedSegment.success ? parsedSegment.data : Segment.B2C;
};

const getSegmentServiceClass = (process: string | null, customer: string | null) => {
  const contractCustomerType = getCustomerType(customer, process);

  if (B2BForsageFeatureFlag) {
    if (process === Process.MOBILITY_SERVICES) {
      return defaultSegmentServiceHousehold;
    }
    if (contractCustomerType === "company" || contractCustomerType === "tradesman") {
      return defaultSegmentServiceCompany;
    } else {
      return defaultSegmentServiceHousehold;
    }
  } else {
    return defaultSegmentServiceHousehold;
  }
};

export const useParsedQueryParams = () => {
  const searchParams = parseHash(useSearchParams());
  const enums = useContext(EnumerationsContext);
  const isContractDraft = useIsContractDraft();

  // Parse all raw query parameters
  const commodity = searchParams.get("commodity");
  const process = searchParams.get("process");
  const product = searchParams.get("product");
  const source = searchParams.get("source");
  const segment = searchParams.get("segment");
  const customer = searchParams.get("customer_type");
  const greenProduct = searchParams.get("green_product");
  const ecoType = searchParams.get("eco_type");
  const tdS01 = searchParams.get("td_s01");
  const tdS02 = searchParams.get("td_s02");
  const tdS03 = searchParams.get("td_s03");
  const tdS04 = searchParams.get("td_s04");
  const sop = searchParams.get("sop");
  const connection = searchParams.get("connection");
  const pds = searchParams.get("pds");
  const supplyPointUsage = searchParams.get("supply_point_usage");
  const region = searchParams.get("region");
  const district = searchParams.get("district");
  const tariff = searchParams.get("tariff");
  const yearConsumptionVt = searchParams.get("year_consumption_vt");
  const yearConsumptionVtUnits = searchParams.get("year_consumption_vt_units");
  const yearConsumptionNt = searchParams.get("year_consumption_nt");
  const yearConsumptionNtUnits = searchParams.get("year_consumption_nt_units");
  const yearConsumption = searchParams.get("year_consumption");
  const yearConsumptionUnits = searchParams.get("year_consumption_units");
  const bpId = searchParams.get("bp_id");
  const pod = searchParams.get("pod");
  const areaSize = searchParams.get("area_size");
  const countInHousehold = searchParams.get("count_in_household");
  const authorId = searchParams.get("author_id");
  const sellChannel = searchParams.get("sell_channel");
  const sellChannelType = searchParams.get("sell_channel_type");
  const contractMethod = searchParams.get("contract_method");
  const crmObjectId = searchParams.get("crm_object_id");
  const validity = searchParams.get("validity");
  const processReason = searchParams.get("process_reason") as ProcessReasonType;
  const requestMethod = searchParams.get("request_method");
  const utmSource = searchParams.get("utm_source");
  const utmMedium = searchParams.get("utm_medium");
  const utmCampaign = searchParams.get("utm_campaign");
  const utmContent = searchParams.get("utm_content");
  const utmTerm = searchParams.get("utm_term");
  const utmId = searchParams.get("utm_id");
  const utmCmpid = searchParams.get("utm_cmpid");
  const sessionId = searchParams.get("session_id");
  const gclid = searchParams.get("gclid");
  const fbclid = searchParams.get("fbclid");
  const bingclid = searchParams.get("bingclid");
  const msclkid = searchParams.get("msclkid");
  const sznclid = searchParams.get("sznclid");
  const operator = searchParams.get("operator");
  const phase = searchParams.get("phase");
  const contractNote = searchParams.get("note");

  return {
    commodity: getCommodity(commodity, enums.commodities),
    customerType: getCustomerType(customer, process),
    process: getProcess(process, enums.processes),
    product: getProduct(
      product,
      enums.products,
      commodity,
      enums.commodities,
      process,
      enums.processes,
      customer,
      isContractDraft
    ),
    greenProduct: getValueInBoolean(greenProduct),
    ecoType,
    tdS01: getValueInBoolean(tdS01),
    tdS02: getValueInBoolean(tdS02),
    tdS03: getValueInBoolean(tdS03),
    tdS04: getValueInBoolean(tdS04),
    sop: getValueInBoolean(sop),
    connection,
    pds,
    supplyPointUsage,
    region,
    district,
    tariff: getTariff(tariff, enums.tariffs),
    yearConsumptionVt: getYearConsumption(yearConsumptionVt, yearConsumptionVtUnits),
    yearConsumptionVtUnits,
    yearConsumptionNt: getYearConsumption(yearConsumptionNt, yearConsumptionNtUnits),
    yearConsumptionNtUnits,
    yearConsumption: getYearConsumption(yearConsumption, yearConsumptionUnits),
    yearConsumptionUnits,
    contractSource: source,
    source,
    bpId,
    pod,
    areaSize,
    countInHousehold,
    authorId,
    sellChannel: getSellChannel(sellChannel, process, enums.processes),
    sellChannelType,
    contractMethod,
    segment: getSegment(segment, customer, process),
    segmentServiceClass: getSegmentServiceClass(process, customer),
    crmObjectId,
    validity,
    processReason,
    requestMethod,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    utmId,
    utmCmpid,
    sessionId,
    gclid,
    fbclid,
    bingclid,
    msclkid,
    sznclid,
    operator,
    contractNote,
    phase,
  };
};

export type ParsedQueryParams = ReturnType<typeof useParsedQueryParams>;
